import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ProjectModel } from '../models/project.model';
import { ProjectUserPermissions } from '../models/project-permissions.model';

@Injectable({
  providedIn: 'root'
})
export class ProjectStateService {
  private currentProjectSubject = new BehaviorSubject<ProjectModel | null>(null);
  private currentProjectIdSubject = new BehaviorSubject<number | null>(null);
  private currentProjectUserActiveSubject = new BehaviorSubject<boolean | null>(null);
  private currentProjectPermissionsSubject = new BehaviorSubject<ProjectUserPermissions | null>(null);
  private projectListRefreshSubject = new Subject<void>();
  private projectUsersRefreshSubject = new Subject<number>();
  private projectListSearchSubject = new BehaviorSubject<string>('');
  private pageSearchSubject = new BehaviorSubject<string>('');
  private mapFilterModeSubject = new BehaviorSubject<'project' | 'user'>('project');
  private mapAllSuggestionsSubject = new BehaviorSubject<string[]>([]);
  private projectListBusinessUnitIdSubject = new BehaviorSubject<number | null>(null);

  // Observable streams
  public currentProject$ = this.currentProjectSubject.asObservable();
  public currentProjectId$ = this.currentProjectIdSubject.asObservable();
  public currentProjectUserActive$ = this.currentProjectUserActiveSubject.asObservable();
  public currentProjectPermissions$ = this.currentProjectPermissionsSubject.asObservable();
  public projectListRefresh$ = this.projectListRefreshSubject.asObservable();
  public projectUsersRefresh$ = this.projectUsersRefreshSubject.asObservable();
  public projectListSearch$ = this.projectListSearchSubject.asObservable();
  public pageSearch$ = this.pageSearchSubject.asObservable();
  public mapFilterMode$ = this.mapFilterModeSubject.asObservable();
  public mapAllSuggestions$ = this.mapAllSuggestionsSubject.asObservable();
  public projectListBusinessUnitId$ = this.projectListBusinessUnitIdSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // Try to restore project from session storage on service initialization (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      this.restoreProjectFromSession();
      this.restoreProjectListBusinessUnitFromSession();
    }
  }

  /**
   * Set the current project and store it in session storage
   */
  setCurrentProject(project: ProjectModel): void {
    console.log('ProjectStateService: Setting current project:', project);
    
    this.currentProjectSubject.next(project);
    this.currentProjectIdSubject.next(project.projectId || null);
    
    // Store in session storage for persistence across page reloads (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('currentProject', JSON.stringify(project));
      sessionStorage.setItem('currentProjectId', project.projectId?.toString() || '');
    }
  }

  /**
   * Set current project user active status and store it in session storage
   */
  setCurrentProjectUserActive(isActive: boolean | null): void {
    this.currentProjectUserActiveSubject.next(isActive);

    if (isPlatformBrowser(this.platformId)) {
      if (isActive === null) {
        sessionStorage.removeItem('currentProjectUserActive');
      } else {
        sessionStorage.setItem('currentProjectUserActive', isActive ? 'true' : 'false');
      }
    }
  }

  setCurrentProjectPermissions(permissions: ProjectUserPermissions | null): void {
    this.currentProjectPermissionsSubject.next(permissions);

    if (isPlatformBrowser(this.platformId)) {
      if (permissions) {
        sessionStorage.setItem('currentProjectPermissions', JSON.stringify(permissions));
      } else {
        sessionStorage.removeItem('currentProjectPermissions');
      }
    }
  }

  /**
   * Set current project by ID only (useful for URL-based navigation)
   */
  setCurrentProjectId(projectId: number): void {
    console.log('ProjectStateService: Setting current project ID:', projectId);
    
    this.currentProjectIdSubject.next(projectId);
    
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('currentProjectId', projectId.toString());
      
      // If we don't have the full project object, clear it
      if (!this.currentProjectSubject.value || this.currentProjectSubject.value.projectId !== projectId) {
        this.currentProjectSubject.next(null);
        sessionStorage.removeItem('currentProject');
      }
    }
  }

  /**
   * Get current project (synchronous)
   */
  getCurrentProject(): ProjectModel | null {
    return this.currentProjectSubject.value;
  }

  /**
   * Get current project ID (synchronous)
   */
  getCurrentProjectId(): number | null {
    return this.currentProjectIdSubject.value;
  }

  /**
   * Clear current project
   */
  clearCurrentProject(): void {
    console.log('ProjectStateService: Clearing current project');
    
    this.currentProjectSubject.next(null);
    this.currentProjectIdSubject.next(null);
    this.currentProjectUserActiveSubject.next(null);
    this.currentProjectPermissionsSubject.next(null);
    
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('currentProject');
      sessionStorage.removeItem('currentProjectId');
      sessionStorage.removeItem('currentProjectUserActive');
      sessionStorage.removeItem('currentProjectPermissions');
    }
  }

  /**
   * Check if a project is currently selected
   */
  hasCurrentProject(): boolean {
    return this.getCurrentProjectId() !== null;
  }

  /**
   * Restore project information from session storage
   */
  private restoreProjectFromSession(): void {
    try {
      const storedProject = sessionStorage.getItem('currentProject');
      const storedProjectId = sessionStorage.getItem('currentProjectId');
      const storedPermissions = sessionStorage.getItem('currentProjectPermissions');

      if (storedProject) {
        const project = JSON.parse(storedProject) as ProjectModel;
        console.log('ProjectStateService: Restored project from session:', project);
        this.currentProjectSubject.next(project);
        this.currentProjectIdSubject.next(project.projectId || null);

        const storedActive = sessionStorage.getItem('currentProjectUserActive');
        if (storedActive === 'true') {
          this.currentProjectUserActiveSubject.next(true);
        } else if (storedActive === 'false') {
          this.currentProjectUserActiveSubject.next(false);
        }

        if (storedPermissions) {
          const permissions = JSON.parse(storedPermissions) as ProjectUserPermissions;
          this.currentProjectPermissionsSubject.next(permissions);
        }
      } else if (storedProjectId) {
        const projectId = parseInt(storedProjectId, 10);
        if (!isNaN(projectId)) {
          console.log('ProjectStateService: Restored project ID from session:', projectId);
          this.currentProjectIdSubject.next(projectId);

          const storedActive = sessionStorage.getItem('currentProjectUserActive');
          if (storedActive === 'true') {
            this.currentProjectUserActiveSubject.next(true);
          } else if (storedActive === 'false') {
            this.currentProjectUserActiveSubject.next(false);
          }

          if (storedPermissions) {
            const permissions = JSON.parse(storedPermissions) as ProjectUserPermissions;
            this.currentProjectPermissionsSubject.next(permissions);
          }
        }
      }
    } catch (error) {
      console.error('ProjectStateService: Error restoring from session storage:', error);
      this.clearCurrentProject();
    }
  }

  /**
   * Update current project with new data (useful for refreshing project info)
   */
  updateCurrentProject(project: ProjectModel): void {
    const currentId = this.getCurrentProjectId();
    if (currentId && project.projectId === currentId) {
      this.setCurrentProject(project);
    }
  }

  notifyProjectListRefresh(): void {
    this.projectListRefreshSubject.next();
  }

  notifyProjectUsersRefresh(projectId?: number | null): void {
    const resolvedProjectId = projectId ?? this.getCurrentProjectId();
    if (resolvedProjectId && resolvedProjectId > 0) {
      this.projectUsersRefreshSubject.next(resolvedProjectId);
    }
  }

  setProjectListSearch(searchText: string): void {
    this.projectListSearchSubject.next(searchText);
  }

  getProjectListSearch(): string {
    return this.projectListSearchSubject.value;
  }

  setPageSearch(text: string): void {
    this.pageSearchSubject.next(text);
  }

  getPageSearch(): string {
    return this.pageSearchSubject.value;
  }

  setMapFilterMode(mode: 'project' | 'user'): void {
    this.mapFilterModeSubject.next(mode);
  }

  getMapFilterMode(): 'project' | 'user' {
    return this.mapFilterModeSubject.value;
  }

  setMapAllSuggestions(suggestions: string[]): void {
    this.mapAllSuggestionsSubject.next(suggestions);
  }

  getMapAllSuggestions(): string[] {
    return this.mapAllSuggestionsSubject.value;
  }

  setProjectListBusinessUnitId(businessUnitId: number | null): void {
    this.projectListBusinessUnitIdSubject.next(businessUnitId);
    if (isPlatformBrowser(this.platformId)) {
      if (businessUnitId == null) {
        sessionStorage.removeItem('projectListBusinessUnitId');
      } else {
        sessionStorage.setItem('projectListBusinessUnitId', businessUnitId.toString());
      }
    }
  }

  getProjectListBusinessUnitId(): number | null {
    return this.projectListBusinessUnitIdSubject.value;
  }

  private restoreProjectListBusinessUnitFromSession(): void {
    try {
      const storedBusinessUnitId = sessionStorage.getItem('projectListBusinessUnitId');
      if (!storedBusinessUnitId) return;
      const parsed = parseInt(storedBusinessUnitId, 10);
      if (!isNaN(parsed)) {
        this.projectListBusinessUnitIdSubject.next(parsed);
      }
    } catch (error) {
      console.error('ProjectStateService: Error restoring project list business unit from session storage:', error);
      this.projectListBusinessUnitIdSubject.next(null);
    }
  }

  hasPermission(pageKey: string, permissionKey: string): boolean {
    const permissions = this.currentProjectPermissionsSubject.value;
    if (!permissions) return false;
    return permissions.permissions?.some(p =>
      p.pageKey === pageKey && p.permissionKey === permissionKey && p.isAllowed
    ) ?? false;
  }

  hasAnyPermissionForPage(pageKey: string): boolean {
    const permissions = this.currentProjectPermissionsSubject.value;
    if (!permissions) return false;
    return permissions.permissions?.some(p => p.pageKey === pageKey && p.isAllowed) ?? false;
  }

  getCurrentProjectPermissions(): ProjectUserPermissions | null {
    return this.currentProjectPermissionsSubject.value;
  }
}
